import { cloneDeep, get, isArray, set } from 'lodash';
import { PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS } from 'src/lib/constants/componentsPath';
import { ART_DER_EIGENLEISTUNG, KOSTEN_ARTEN, KOSTEN_POSITION } from 'src/lib/constants/constant';
import { FormulasResultType } from 'src/lib/types/types';
import { addIndexesToPath, getFieldNamefromDataPath, getIDFromResponseOption, groupKostenByPosition, isTrue } from 'src/utils/helper';
import { calculateGesamtSumme, calculateProjektDauer, calculateProjektStartJahr } from '../commonFormulas';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
import KostenPositionSchema from 'src/lib/paths/kostenposition_FULL_Paths.json';
import LeistungsGruppeSchema from 'src/lib/paths/leistungsgruppe_FULL_Paths.json';
const P = AntragSchema.attributes;
const KP_P = KostenPositionSchema.attributes;
const LG_P = LeistungsGruppeSchema.attributes;

export const kreativwirtschaftFormulas = (
	application: any,
	vonTag: number,
	vonMonat: string,
	bisTag: number,
	bisMonat: string,
	kostenKategories: any,
	leistungsgruppe: any,
) => {
	let appCopy = cloneDeep(application);
	let result: any[] = [];
	const kostenKategoriesGroup = groupKostenByPosition(kostenKategories);

	let calcultedProjkJahr = calculateProjektStartJahr(appCopy, vonTag, vonMonat);
	let calcultedProjkdauer = calculateProjektDauer(appCopy, vonTag, vonMonat, bisTag, bisMonat);
	if (isArray(calcultedProjkJahr)) {
		for (let ele of calcultedProjkJahr) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	if (isArray(calcultedProjkdauer)) {
		for (let ele of calcultedProjkdauer) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	result.push(...calcultedProjkJahr);
	result.push(...calcultedProjkdauer);

	if (get(kostenKategoriesGroup, KOSTEN_POSITION.Ausgabe, []).includes(KOSTEN_ARTEN.Personalkosten)) {
		let calculatedPersonalKosten: any[] = [];
		calculatedPersonalKosten = calculatePersonalKosten(appCopy, leistungsgruppe, get(appCopy, P.Allgemein_Projektstart_Jahr.path, 0));
		for (let ele of calculatedPersonalKosten) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
		result.push(...calculatedPersonalKosten);
	}

	if (get(kostenKategoriesGroup, KOSTEN_POSITION.Ausgabe, []).includes(KOSTEN_ARTEN.Sachkosten)) {
		let calculatedSachKosten = calculateSachkosten(appCopy);
		for (let ele of calculatedSachKosten) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
		result.push(...calculatedSachKosten);
	}

	let filledKostenProJahrBlock = fillKostenProJahrBlick(appCopy, get(appCopy, P.Allgemein_Projektstart_Jahr.path, 0));
	for (let ele of filledKostenProJahrBlock) {
		set(appCopy, get(ele, 'path'), get(ele, 'value'));
	}
	result.push(...filledKostenProJahrBlock);

	let calculatedSumme = calculateSummenUndFoerderquote(appCopy);
	for (let ele of calculatedSumme) {
		set(appCopy, get(ele, 'path'), get(ele, 'value'));
	}
	result.push(...calculatedSumme);

	let calculatedGesamtSummeSkizze = calculateGesamtSumme(appCopy);
	if (isArray(calculatedGesamtSummeSkizze)) {
		for (let ele of calculatedGesamtSummeSkizze) {
			set(appCopy, get(ele, 'path'), get(ele, 'value'));
		}
	}
	result.push(...calculatedGesamtSummeSkizze);

	return result;
};

function calculatePersonalKosten(application: any, leistungsgruppe: any, projektJahrStart: number) {
	let result: any = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);
	for (let i = 0; i < allKostenPosition.length; i++) {
		const eachKostenPosition = allKostenPosition[i];

		if (get(eachKostenPosition, KP_P.Allgemein_Kostenart_Finanzierung.path, '') === KOSTEN_ARTEN.Personalkosten) {
			const KalkulationPersonalKosten = getTransformedPersonalKosten(eachKostenPosition, leistungsgruppe, projektJahrStart);

			result.push({ path: `${P.Foerderantrag_Kostenpositions.path}.${i}`, value: KalkulationPersonalKosten });
		}
	}

	return result;
}
function calculateSachkosten(application: any) {
	let result: any = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);
	for (let i = 0; i < allKostenPosition.length; i++) {
		const eachKostenPosition = allKostenPosition[i];

		if (get(eachKostenPosition, KP_P.Allgemein_Kostenart_Finanzierung.path) === KOSTEN_ARTEN.Sachkosten) {
			const einzelKosten = get(eachKostenPosition, KP_P.Sachausgaben_Kreativwirtschaft.schema.attributes.Einzelkosten.path);
			const anzahl = get(eachKostenPosition, KP_P.Sachausgaben_Kreativwirtschaft.schema.attributes.Anzahl.path);
			const einzelKostenNum = parseFloat(einzelKosten) || 0;
			const anzahlNum = parseFloat(anzahl) || 0;
			const gesamtSumme = einzelKostenNum * anzahlNum;
			result.push({
				path: addIndexesToPath(
					P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Geplante_Gesamtsumme.path,
					[`${i}`],
					['Foerderantrag_Kostenpositions'],
				),
				value: isFinite(gesamtSumme) ? gesamtSumme : 0,
			});
		}
	}

	return result;
}
function calculateSummenUndFoerderquote(application: any) {
	let result: FormulasResultType[] = [];
	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);

	const allAusgaben_Arr = allKostenPosition.filter((ele: any) => get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Ausgabe);
	const allEinnahmen_Arr = allKostenPosition.filter((ele: any) => get(ele, KP_P.Allgemein_Kostenposition.path) === KOSTEN_POSITION.Einnahme);

	const ausgabenGesamt = allAusgaben_Arr.reduce((acc: any, ele: any) => {
		return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
	}, 0);
	const einnahmenGesamt = allEinnahmen_Arr.reduce((acc: any, ele: any) => {
		return acc + parseFloat(get(ele, KP_P.Allgemein_Geplante_Gesamtsumme.path, '0.0'));
	}, 0);
	const zuwendungGesamt = ausgabenGesamt ? ausgabenGesamt - einnahmenGesamt : 0;
	const foerderQuote = ausgabenGesamt > 0 && isFinite(zuwendungGesamt) && isFinite(ausgabenGesamt) ? (zuwendungGesamt / ausgabenGesamt) * 100 : 0;

	result.push(
		{
			path: P.Orga_Foerderquote_Gesamt.path,
			value: !isNaN(foerderQuote) ? foerderQuote.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Geplante_Projektausgaben.path,
			value: !isNaN(ausgabenGesamt) ? ausgabenGesamt.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Eingebrachte_Eigen_Und_Drittmittel.path,
			value: !isNaN(einnahmenGesamt) ? einnahmenGesamt.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Beantragte_Zuwendung.path,
			value: !isNaN(zuwendungGesamt) ? zuwendungGesamt.toFixed(2) : 0,
		},
		{
			path: P.Bewilligung_Foerderquote_Gesamt.path,
			value: !isNaN(foerderQuote) && isFinite(foerderQuote) ? foerderQuote.toFixed(2) : 0,
		},
		{
			path: P.Allgemein_Foerderquote_Gesamt.path,
			value: !isNaN(foerderQuote) && isFinite(foerderQuote) ? foerderQuote.toFixed(2) : 0,
		},
		{
			path: P.Antragstellende_Organisation_Foerderquote.path,
			value:
				!isNaN(foerderQuote) && isFinite(foerderQuote) ?
					[{ Projektjahr: get(application, P.Allgemein_Projektstart_Jahr.path), Foerderquote_403741: foerderQuote.toFixed(2) }]
				:	[],
		},
		{
			path: P.Bewilligung_Jahresaufteilung.path,
			value:
				!isNaN(foerderQuote) && isFinite(foerderQuote) ?
					[
						{
							Projektjahr: get(application, P.Allgemein_Projektstart_Jahr.path),
							Ausgaben: !isNaN(ausgabenGesamt) ? ausgabenGesamt.toFixed(2) : 0,
							Eigen_45_Und_Drittmittel: !isNaN(einnahmenGesamt) ? einnahmenGesamt.toFixed(2) : 0,
							Zuwendung: !isNaN(zuwendungGesamt) ? zuwendungGesamt.toFixed(2) : 0,
							Foerderquote_403741: foerderQuote.toFixed(2),
						},
					]
				:	[],
		},
	);
	return result;
}
function fillKostenProJahrBlick(application: any, projektJahrStart: number) {
	let result: any = [];

	const allKostenPosition = get(application, P.Foerderantrag_Kostenpositions.path, []);

	for (let i = 0; i < allKostenPosition.length; i++) {
		const eachKostenPosition = allKostenPosition[i];

		const geplanteSumme = get(eachKostenPosition, KP_P.Allgemein_Geplante_Gesamtsumme.path);

		result.push({
			path: addIndexesToPath(
				P.Foerderantrag_Kostenpositions.schema.attributes.Allgemein_Kosten_Pro_Jahr.path,
				[`${i}`],
				['Foerderantrag_Kostenpositions'],
			),
			value: [
				{
					Zelle_1: projektJahrStart,
					Zelle_2: !isNaN(geplanteSumme) ? geplanteSumme : 0,
				},
			],
		});
	}

	return result;
}

/**************************************************************************************************************
 * ************************************ HELPER FUNCTIONS ******************************************************
 * ************************************************************************************************************
 */
const getTransformedPersonalKosten = (eachKostenPosition: any, leistungsgruppe: any, projektJahrStart: number) => {
	const selectedEigenLeistung = get(
		eachKostenPosition,
		KP_P.Personalausgaben_Kreativwirtschaft.schema.attributes.Kreativwirtschaft_Art_Der_Eigenleistung.path,
		'',
	);

	const transformAllgemeinKostenProJahr = (personalAusgabenKreativBlock: any) => {
		let result: any[] = [];
		const errechneteBetrag = get(personalAusgabenKreativBlock, PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.ErrechnetePauschale);
		result = [
			{
				Zelle_1: projektJahrStart,
				Zelle_2: !isNaN(errechneteBetrag) ? errechneteBetrag : 0,
			},
		];
		if (get(personalAusgabenKreativBlock, 'id')) set(result, 'id', get(personalAusgabenKreativBlock, 'id'));
		return result;
	};

	const calculatePersonalausgabenKreativwirtschaft = (personalAusgabenKreativBlock: any) => {
		/**
		 * Wenn UnbareEigenleistung ausgewählt ist
		 */
		if (selectedEigenLeistung === ART_DER_EIGENLEISTUNG.UnbareEigenleistung) {
			const currentJahr = new Date().getFullYear();
			const leistungsgruppeMindeslosh = leistungsgruppe.find(
				(ele: any) =>
					get(ele, LG_P.Allgemein_Gilt_Fuer_Jahr.path) === currentJahr &&
					isTrue(get(ele, LG_P.Allgemein_Aktiv.path)) &&
					isTrue(get(ele, LG_P.Allgemein_ist_Mindestlohn.path)),
			);

			const stundenSatz = get(leistungsgruppeMindeslosh, LG_P.Allgemein_Mindestlohn_In_EUR.path, 0);
			const anzahlStuden = get(personalAusgabenKreativBlock, PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.GeplanteStundenImVorhaben, 0);
			const stundenSatzNum = parseFloat(stundenSatz) || 0;
			const anzahlStudenNum = parseFloat(anzahlStuden) || 0;

			const gesamtSumme = stundenSatzNum * anzahlStudenNum;

			return {
				...personalAusgabenKreativBlock,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Leistungsgruppe]: get(leistungsgruppeMindeslosh, 'id'),
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.StundenSatz]: stundenSatz,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Einsatzmonate]: null,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.ErrechneteMaxEinsatzStunden]: anzahlStuden,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.ErrechnetePauschale]: 0,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.ErrechnetePauschale]: isFinite(gesamtSumme) ? gesamtSumme : 0,
			};
			/**
			 * Wenn BareEigenleistung ausgewählt ist
			 */
		} else if (selectedEigenLeistung === ART_DER_EIGENLEISTUNG.BareEigenleistung) {
			const selectedLeistungsGruppeID = getIDFromResponseOption(
				get(personalAusgabenKreativBlock, PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Leistungsgruppe),
			);
			const selectedLeistungsGruppe_Obj = leistungsgruppe.find(
				(ele: any) => get(ele, 'id') === selectedLeistungsGruppeID && !isTrue(get(ele, LG_P.Allgemein_ist_Mindestlohn.path)),
			);

			let stundenSatz = get(selectedLeistungsGruppe_Obj, LG_P.Allgemein_Personalkosten_Inkl_Arbeitsplatzkosten_pro_Stunde.path, 0);
			// const einsatzMonate = get(personalAusgabenKreativBlock, PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Einsatzmonate, null);
			const anzahlGeplannteStunde = get(
				personalAusgabenKreativBlock,
				PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.GeplanteStundenImVorhaben,
				0,
			);

			const stundenSatzNum = parseFloat(stundenSatz) || 0;
			const anzahlGeplannteStundeNum = parseFloat(anzahlGeplannteStunde) || 0;
			const ErrechnetePauschale = stundenSatzNum * anzahlGeplannteStundeNum;

			return {
				...personalAusgabenKreativBlock,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Leistungsgruppe]: selectedLeistungsGruppe_Obj || null,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.StundenProWoche]: 0,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.StundenSatz]: stundenSatz,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.StellanteilImVorhaben]: 0,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Einsatzmonate]: null,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.ErrechnetePauschale]: isFinite(ErrechnetePauschale) ? ErrechnetePauschale : 0,
			};

			/**
			 * Wenn Sozialversicherungspflichtiges Personal ausgewählt ist
			 */
		} else if (selectedEigenLeistung === ART_DER_EIGENLEISTUNG.SozialversicherungspflichtigesPersonal) {
			//
			// wenn selectedEigenleistung ist Sozialversicherungspflichtiges Personal
			//
			const selectedLeistungsGruppeID = getIDFromResponseOption(
				get(personalAusgabenKreativBlock, PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Leistungsgruppe),
			);
			const selectedLeistungsGruppe_Obj = leistungsgruppe.find(
				(ele: any) => get(ele, 'id') === selectedLeistungsGruppeID && !isTrue(get(ele, LG_P.Allgemein_ist_Mindestlohn.path)),
			);

			const currentJahr = new Date().getFullYear();
			const leistungsgruppeMindeslosh = leistungsgruppe.find(
				(ele: any) =>
					get(ele, LG_P.Allgemein_Gilt_Fuer_Jahr.path) === currentJahr &&
					isTrue(get(ele, LG_P.Allgemein_Aktiv.path)) &&
					isTrue(get(ele, LG_P.Allgemein_ist_Mindestlohn.path)),
			);
			const maxFoerderStunden = Number(get(leistungsgruppeMindeslosh, LG_P.Allgemein_maximal_Zu_Foerdernde_Stunden_Pro_Jahr.path, 1592));

			let stundenSatz = 0;

			const einsatzMonate = get(personalAusgabenKreativBlock, PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Einsatzmonate, null);
			const eingegebeneStundenProWoche = get(personalAusgabenKreativBlock, PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.StundenProWoche, 0);
			const stelleAnteilImVorhaben = get(
				personalAusgabenKreativBlock,
				PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.StellanteilImVorhaben,
				0,
			);
			const AnzahlDerGeplantenStundenImProjekt = get(
				personalAusgabenKreativBlock,
				PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.GeplanteStundenImVorhaben,
				0,
			);

			/**
			 * Laut dem Ticket HA-1681 muss für Sozialversicherungspflichtiges Personal
			 * immer der reguläre Stundensatz genommen werden, auch wenn die Stunden pro Woche
			 * unter 38.5 liegen.
			 */
			// if (Number(eingegebeneStundenProWoche) > 38.5 || Number(eingegebeneStundenProWoche) === 0) {
			stundenSatz = get(selectedLeistungsGruppe_Obj, LG_P.Allgemein_Personalkosten_Inkl_Arbeitsplatzkosten_pro_Stunde.path, 0);
			// } else {
			// 	stundenSatz = get(selectedLeistungsGruppe_Obj, LG_P.Allgemein_Personalkosten_0221163365_ten_pro_Stunde_385_Std_Woche.path, 0);
			// }

			/**
			 * Laut dem Ticket HA-2211 soll die Maximale EInsatzstunden in i-taros eingetragen werden.
			 * Diese Zahl wird in einer Leistungsgruppe mit Mindstlohn "ja" gesetzt.
			 */
			// ES IST WIEDER WAS GEÄNDERT.
			/**
			 * Laut dem Ticket HA-2211 soll die Errechnete Pauschale wieder berechnet werden.
			 */
			const ErrechneteMaxEinsatzStunden =
				maxFoerderStunden * (eingegebeneStundenProWoche / 40) * (stelleAnteilImVorhaben / 100) * (einsatzMonate / 12);
			// const ErrechneteMaxEinsatzStunden = get(leistungsgruppeMindeslosh, LG_P.Allgemein_maximal_Zu_Foerdernde_Stunden_Pro_Jahr.path, 0);

			const ErrechnetePauschale = stundenSatz * AnzahlDerGeplantenStundenImProjekt;

			return {
				...personalAusgabenKreativBlock,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Leistungsgruppe]: selectedLeistungsGruppe_Obj || null,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.StundenSatz]: stundenSatz,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.Einsatzmonate]: einsatzMonate,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.ErrechneteMaxEinsatzStunden]:
					isFinite(ErrechneteMaxEinsatzStunden) ? ErrechneteMaxEinsatzStunden : 0,
				[PERSONALAUSGABEN_KREATIVWIRTSCHAFT_COMPONENT_PATHS.ErrechnetePauschale]: isFinite(ErrechnetePauschale) ? ErrechnetePauschale : 0,
			};
		}

		return personalAusgabenKreativBlock;
	};

	const calculatedPersonalAusgaben = calculatePersonalausgabenKreativwirtschaft(
		get(eachKostenPosition, KP_P.Personalausgaben_Kreativwirtschaft.path, {}),
	);
	const calculatedAllgemeinKostenProJahr = transformAllgemeinKostenProJahr(calculatedPersonalAusgaben);

	return {
		...eachKostenPosition,
		attributes: {
			...eachKostenPosition.attributes,
			[getFieldNamefromDataPath(KP_P.Allgemein_Bezeichnung.path)]: get(
				eachKostenPosition,
				KP_P.Personalausgaben_Kreativwirtschaft.schema.attributes.Kreativwirtschaft_Name_Desder_Beschaeftigten.path,
				'',
			),
			[getFieldNamefromDataPath(KP_P.Allgemein_Kosten_Pro_Jahr.path)]: calculatedAllgemeinKostenProJahr,
			[getFieldNamefromDataPath(KP_P.Personalausgaben_Kreativwirtschaft.path)]: calculatedPersonalAusgaben,
			[getFieldNamefromDataPath(KP_P.Allgemein_Geplante_Gesamtsumme.path)]:
				calculateAllgemeinKostenProJahrGesamtSumme(calculatedAllgemeinKostenProJahr),
		},
	};
};

function calculateAllgemeinKostenProJahrGesamtSumme(dataArray: any[]) {
	const total = dataArray.reduce((sum: any, item: any) => {
		// Convert the Zelle_2 value to a number and add it to the sum
		return sum + parseFloat(get(item, 'Zelle_2', '0.0'));
	}, 0);

	// Return the total formatted to 2 decimal places
	return !isNaN(total) ? total.toFixed(2) : 0;
}
